import {
    Button,
    Flex,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { BusinessFocus } from './BusinessFocus';
import { ServicesWeProvide } from './ServicesWeProvide';
import * as S from './MainInformation.style';
import { ProjectsPublished } from './ProjectsPublished';
import { AboutDescription } from '../about-description/AboutDescription';
import { Divider } from './Divider';
import { Associations } from '../associations/Associations';
import type { LocationDataType } from '@modules/professional/type/contact-page-types';
import { Locations } from '../locations/Locations';
import { SocialLinks } from './SocialLinks';
import { YearFounded } from './YearFounded';
import { YearJoined } from './YearJoined';
import { ProfessionalPhoneNumbers } from './ProfessionalPhoneNumbers';
import { useCurrentTabBannerImage } from '~/modules/professional/hook/use-current-tab-banner-image';
import { useProfessionalInitialRouteData } from '~/modules/professional/hook/use-professional-initial-route-data';
import { ProductsListed } from './ProductsListed';
import { ExternalLinkLine, HeartFill, HeartLine } from '@archipro-design/icons';
import { Check } from 'lucide-react';
import { useAppData } from '@modules/root';
import Availability from './Availability';
import { FollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { isUserLoggedIn, useUser } from '@modules/user';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import { useEnquirySubmitted } from '~/modules/tracking/hook/useEnquirySubmitted';
import { EnquiryInline } from '~/modules/enquiry/component/enquiry-inline/EnquiryInline';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { PublicKPI } from './PublicKPI';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';

interface MainInformationProps {
    locations: LocationDataType[];
    responseRate: number;
    calculateDistance: () => Promise<boolean>;
    showProducts: boolean;
    disabledSocialLinks?: boolean;
    disabledSendEnquiry?: boolean;
}

export const MainInformation: React.FC<MainInformationProps> = ({
    locations,
    calculateDistance,
    showProducts,
    disabledSocialLinks,
    disabledSendEnquiry,
}) => {
    const professional = useProfessionalInitialRouteData();
    const { Banners: banners } = professional;
    const bannerImage = useCurrentTabBannerImage(banners);
    const { mobile: isMobile } = useAppDisplayModeContext();
    const { css } = useStyles({
        hasBannerImage: !!bannerImage,
    });
    const { isInDesignBoard } = useHandleFollow();
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { guestSave } = useGuestSave();
    const { availableForWorkEnabled } = useAppData();
    const availableForWork =
        professional.AvailableForWork && availableForWorkEnabled;
    const { showEnquiryCTA, showEnquiryForm, showSpecifierLoginPrompt } =
        useEnquiryVisibility(professional);

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(professional.Website, {
            libraryItemID: professional.ID,
            professionalID: professional.ID,
            professionalTitle: professional.Title,
            source: 'ProfessionalOverview',
        });

    const { hasVisited, markVisited } = useWebsiteVisited(professional.ID);
    const { hasSubmitted } = useEnquirySubmitted(professional.ID);

    const { openEnquiryModal } = useEnquiryModal({
        professional: professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        source: 'ProfessionalOverview',
        itemTitle: professional.Title,
        includeBranch: true,
    });

    const enquiryLabel =
        professional.primaryCTA?.primaryCTALabelTitleCase ?? 'Send Enquiry';

    return isMobile ? (
        <div className={css(S.MobileInfoContainer)}>
            <AboutDescription />
            <Flex column className={css(S.Contacts)}>
                <div className={css(S.EnquireContainer)}>
                    <Flex>
                        {showEnquiryCTA && (
                            <Button
                                size={16}
                                color="dark"
                                className={css(S.EnquireButton)}
                                onClick={() => {
                                    openEnquiryModal({
                                        title: enquiryLabel,
                                        ctaLabel: enquiryLabel,
                                    });
                                }}
                                variables={{
                                    darkButtonBackgroundColorNormal: '#222222',
                                    darkOutlinedButtonBorderNormal: `1px solid #222222`,
                                }}
                            >
                                {enquiryLabel}
                                {hasSubmitted && (
                                    <Check className="text-green-600 ml-2 h-4 w-4 animate-tick-pop-in" />
                                )}
                            </Button>
                        )}

                        {(() => {
                            const isSaved = isInDesignBoard(
                                professional.ID,
                                'ProfessionalPin',
                            );
                            const heartButton = (
                                <Button
                                    className={css(S.ButtonHeart)}
                                    transparent
                                    size={20}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (!isLoggedIn) {
                                            guestSave({
                                                pinId: professional.ID,
                                                pinType: 'ProfessionalPin',
                                            });
                                        }
                                    }}
                                    icon={
                                        isSaved ? (
                                            <HeartFill size="2x" />
                                        ) : (
                                            <HeartLine size="2x" />
                                        )
                                    }
                                    variables={{
                                        iconPadding: 0,
                                        buttonPadding: 0,
                                    }}
                                />
                            );
                            return isLoggedIn ? (
                                <FollowSelector
                                    trigger={heartButton}
                                    pinId={professional.ID}
                                    pinType="ProfessionalPin"
                                />
                            ) : (
                                heartButton
                            );
                        })()}
                    </Flex>
                </div>
                <Flex
                    gap="space-20"
                    className={css(S.SaveShareContainer)}
                    hAlign="center"
                    vAlign="start"
                >
                    <Button
                        as={'a'}
                        href={referralTrackingURL}
                        target="_blank"
                        size={16}
                        color="dark"
                        transparent
                        outlined
                        variables={{
                            buttonSize20LineHeight: '130%',
                            buttonPadding: pxToRem(15),
                        }}
                        className={css(S.ViewWebsiteButton)}
                        {...referralTrackingParams}
                        onClick={markVisited}
                    >
                        VISIT WEBSITE
                        {hasVisited ? (
                            <Check
                                style={{
                                    width: pxToRem(16),
                                    height: pxToRem(16),
                                    marginLeft: pxToRem(8),
                                    marginTop: pxToRem(-2),
                                }}
                                className="text-green-600 inline-block animate-tick-pop-in"
                            />
                        ) : (
                            <ExternalLinkLine
                                styles={{
                                    width: pxToRem(16),
                                    height: pxToRem(16),
                                    '& > svg': {
                                        marginLeft: pxToRem(8),
                                        marginTop: pxToRem(-2),
                                    },
                                }}
                            />
                        )}
                    </Button>
                </Flex>
            </Flex>

            <BusinessFocus />
            <ServicesWeProvide />
            {showProducts ? <ProductsListed /> : <ProjectsPublished />}

            <YearFounded />
            <YearJoined />
            <PublicKPI />

            <Associations />

            {locations.length > 0 && (
                <Locations
                    locations={locations}
                    calculateDistance={calculateDistance}
                    professionalID={professional.ID}
                />
            )}
            <ProfessionalPhoneNumbers />
            {availableForWork && <Availability />}

            <SocialLinks />
        </div>
    ) : (
        <div className={css(S.MainInfoContainer)}>
            <Flex space="between">
                <div className={css(S.LeftSection)}>
                    {professional.CompanyLogo && (
                        <div className="flex items-center gap-5 pb-7.5">
                            <div
                                className="flex h-25 w-25 shrink-0 items-center justify-center overflow-hidden"
                                style={{
                                    backgroundColor:
                                        professional.LogoBackgroundColor,
                                }}
                            >
                                <img
                                    src={professional.CompanyLogo}
                                    alt={`${professional.Title} company logo`}
                                    className="h-full w-full object-contain"
                                    loading="eager"
                                />
                            </div>
                            <div className="flex flex-col">
                                <h2 className="text-lg font-medium tracking-wide text-black">
                                    {professional.Title}
                                </h2>
                                {professional.LibraryLocations.length > 0 &&
                                    professional.LibraryLocations[0] && (
                                        <span className="text-sm text-gray-500">
                                            {[
                                                professional.LibraryLocations[0]
                                                    .Suburb,
                                                professional.LibraryLocations[0]
                                                    .Region,
                                            ]
                                                .filter(
                                                    (x) =>
                                                        (x ?? '').trim()
                                                            .length > 0,
                                                )
                                                .join(', ')}
                                            {professional.LibraryLocations
                                                .length > 1 &&
                                                ` + ${professional.LibraryLocations.length - 1} more`}
                                        </span>
                                    )}
                            </div>
                        </div>
                    )}
                    <BusinessFocus />
                    <ServicesWeProvide />
                    {showProducts ? <ProductsListed /> : <ProjectsPublished />}
                    <Divider />
                </div>
                <div className={css(S.RightSection)}>
                    <AboutDescription />
                    <YearFounded />
                    <YearJoined />
                    <PublicKPI />
                    <Associations />
                    <SocialLinks disabledSocialLinks={disabledSocialLinks} />
                    <Locations
                        locations={locations}
                        calculateDistance={calculateDistance}
                        professionalID={professional.ID}
                    />
                    <ProfessionalPhoneNumbers />
                    {availableForWork && <Availability />}
                    {(showEnquiryForm || showSpecifierLoginPrompt) && (
                        <>
                            <Divider />
                            <h3
                                className="mt-14 pb-6 text-lg font-medium tracking-tight uppercase"
                                style={{
                                    fontFamily:
                                        "'neue-haas-grotesk-text', sans-serif",
                                    letterSpacing: '-0.02em',
                                    color: '#343434',
                                }}
                            >
                                GET IN TOUCH
                            </h3>
                            {showSpecifierLoginPrompt && (
                                <SpecifierLoginPrompt
                                    professionalName={professional.Title}
                                    itemDescription="this professional"
                                    showBottomBorder={false}
                                    className="pt-0"
                                />
                            )}
                            {showEnquiryForm && (
                                <EnquiryInline
                                    professional={professional}
                                    disableSubmit={disabledSendEnquiry}
                                    includeBranch
                                    showCustomEnquiryText
                                    submitButtonText={
                                        professional.primaryCTA
                                            ?.primaryCTALabelTitleCase ??
                                        'Send Enquiry'
                                    }
                                    siteTreeID={professional.ID}
                                    itemType="professional"
                                    itemTitle={professional.Title}
                                    ctaLabel={enquiryLabel}
                                />
                            )}
                        </>
                    )}
                </div>
            </Flex>
        </div>
    );
};
